/**
 * What an ingest is doing, right now.
 *
 * It lives in its own file because it belongs on more than the account card:
 * the browse screens are the first place a new user goes, and while the first
 * fetch is still running they would otherwise show a bare "nothing here" —
 * which is both wrong and, on a 90k-channel panel, the first thing anyone sees.
 */

import type { AccountSummary, IngestProgress, Kind } from '@sflx/shared';
import { formatCount } from '../lib/format';

export const KIND_LABEL: Record<Kind, string> = { live: 'Live', movie: 'Movies', series: 'Series' };
export const KIND_NOUN: Record<Kind, string> = { live: 'channels', movie: 'movies', series: 'series' };

export function isRunningJob(job: IngestProgress): boolean {
  return job.status === 'queued' || job.status === 'running';
}

export function IngestBanner({ job, account }: { job: IngestProgress; account?: AccountSummary | null }) {
  // The country selection narrows what is LISTED, not what is fetched — the
  // catalog is pulled whole and filtered in SQL. Appending the codes to the
  // count read as "1,204 channels (US, CA)", which claims a scope this number
  // does not have.
  const scope =
    job.kind === 'live' && account?.liveCountryCodes?.length
      ? `filtering to ${account.liveCountryCodes.join(', ')}`
      : null;
  const ratio = job.categoriesTotal > 0 ? Math.min(1, job.categoriesDone / job.categoriesTotal) : 0;

  return (
    <div className="notice stack stack--tight">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <span>
          <strong>{KIND_LABEL[job.kind]}:</strong> {formatCount(job.streamsWritten)} {KIND_NOUN[job.kind]} so far
        </span>
        <span className="muted mono">
          {job.status === 'queued'
            ? 'queued'
            : `${formatCount(job.categoriesDone)}/${formatCount(job.categoriesTotal)} categories`}
        </span>
      </div>
      <div className="progressbar">
        <div className="progressbar__fill" style={{ width: `${Math.round(ratio * 100)}%` }} />
      </div>
      {scope ? <span className="muted">{scope} once it lands</span> : null}
    </div>
  );
}

/**
 * The empty state a browse screen shows while its catalog is still arriving.
 */
export function IngestingNotice({ job }: { job: IngestProgress }) {
  return (
    <div className="stack" style={{ padding: 'var(--s-5)' }}>
      <IngestBanner job={job} />
      <span className="muted">
        {KIND_NOUN[job.kind].charAt(0).toUpperCase() + KIND_NOUN[job.kind].slice(1)} are still arriving. This screen
        fills itself in as they land.
      </span>
    </div>
  );
}
